import { Avatar, Box, Card, styled, useTheme } from '@mui/material';
import { H3, Small } from 'components/Typography';
import { ChevronDown, UserPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import FlexRowAlign from 'components/flexbox/FlexRowAlign';

const UserProfileCard = ({
  isTablet,
  isMobile,
  userName = '홍길동',
  userGrade = 'BRONZE 1',
  tagline = '나만의 길을 찾고자 걸어가는 마이로드^^',
  email = 'zzzz@naver.com',
  joinDate = '2025.10.13',
  profileProgress = 85, // Progress percentage (0-100)
  stats = {
    followers: 867,
    following: 341,
    posts: 43,
    comments: 76,
  },
  avatarUrl = '/static/avatar/001-man.svg',
  ...props
}) => {
  const theme = useTheme();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
  const shareDropdownRef = useRef(null);

  const handleTooltipToggle = () => {
    if (isMobile) {
      setTooltipOpen(!tooltipOpen);
    }
  };

  const handleShareMenuToggle = () => {
    setShareDropdownOpen(!shareDropdownOpen);
  };

  const handleShareMenuClose = () => {
    setShareDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        shareDropdownRef.current &&
        !shareDropdownRef.current.contains(event.target)
      ) {
        setShareDropdownOpen(false);
      }
    };

    if (shareDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [shareDropdownOpen]);

  let padding;
  if (window.innerWidth > 1720 && !isTablet) {
    padding = '0px 0px';
  } else {
    padding = isTablet ? '0px 15px' : '0px 0px';
  }

  const StyledCard = styled(Card)(({ theme }) => ({
    width: '100%',
    padding: padding,
    backgroundColor: 'transparent',
    borderRadius: 0,
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      '& > .MuiBox-root': {
        paddingBottom: 0,
      },
    },
  }));

  const StatItem = ({ label, value, labelColor, valueColor, hideValue }) => (
    <Box
      sx={{
        textAlign: 'left',
        minWidth: isMobile ? '30px' : isTablet ? '45px' : '60px',
        maxWidth: isTablet ? 'calc(25% - 6px)' : 'none',
        marginRight: isMobile ? '8px' : isTablet ? '8px' : '20px',
        display: !isTablet ? 'flex' : 'block',
        alignItems: !isTablet ? 'center' : 'unset',
        gap: !isTablet ? '4px' : 0,
        flex: isTablet || isMobile ? '1 1 0' : 'none',
      }}
    >
      <Small
        sx={{
          color: labelColor || theme.palette.primary.greytxt,
          fontSize: isMobile ? '11px' : !isTablet ? '14.5px' : '13px',
          display: 'block',
          marginBottom: !isTablet ? 0 : '2px',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Small>
      {!hideValue && value && (
        <Small
          sx={{
            fontSize: isMobile ? '12px' : !isTablet ? '14.5px' : '13px',
            fontWeight: '600',
            color: valueColor || theme.palette.text.primary,
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Small>
      )}
    </Box>
  );

  // Small Circular Progress Component
  const SmallCircularProgress = ({ progress, size = 40, showText = false }) => {
    const radius = (size - 4) / 2; // Account for stroke width
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          width={size}
          height={size}
          style={{
            position: 'absolute',
            transform: 'rotate(-90deg)',
          }}
        >
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#E0E0E0"
            strokeWidth="3"
            fill="transparent"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={theme.palette.primary.main}
            strokeWidth="3"
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 0.5s ease-in-out',
            }}
          />
        </svg>
        {/* Progress percentage text - only show if showText is true */}
        {showText && (
          <Small
            sx={{
              fontSize: '10px',
              fontWeight: '600',
              color: theme.palette.primary.main,
            }}
          >
            {progress}%
          </Small>
        )}
      </Box>
    );
  };

  return (
    <StyledCard
      sx={{
        border: 'none',
        overflow: 'visible',
        ...props.sx,
      }}
    >
      <Box
        sx={{
          width: '100%',
        }}
      >
        {/* Main Card Content */}
        <Box
          sx={{
            backgroundColor: '#fff',
            padding: isMobile ? '18px' : '26px',
            borderRadius: '24px',
            boxShadow: '1px 4px 6px rgba(0, 0, 0, 0.05)',
          }}
        >
          {/* Header with Avatar and User Info */}
          <Box sx={{ mb: 2 }}>
            <FlexRowAlign
              mb={isTablet ? 2 : 0}
              justifyContent="space-between"
              width="100%"
            >
              <FlexRowAlign>
                {/* Avatar with Progress Ring */}
                <Box sx={{ marginRight: '16px', position: 'relative' }}>
                  <SmallCircularProgress
                    progress={profileProgress}
                    size={isMobile ? 70 : 80}
                  />
                  <Avatar
                    src={avatarUrl}
                    sx={{
                      width: isMobile ? 60 : 70,
                      height: isMobile ? 60 : 70,
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      border: '2px solid white',
                    }}
                  />
                </Box>

                {/* User Info */}
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '4px',
                      mb: 0.5,
                    }}
                  >
                    <H3
                      sx={{
                        fontSize: isMobile ? '16px' : '20px',
                        fontWeight: '600',
                        flexShrink: 0,
                      }}
                    >
                      {userName}님은
                    </H3>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box
                        component="img"
                        src="/static/icons/bronze-stamp.png"
                        sx={{
                          width: isMobile ? '18px' : '24px',
                          height: isMobile ? '18px' : '24px',
                          flexShrink: 0,
                        }}
                      />
                      <Small
                        sx={{
                          fontSize: isMobile ? '16px' : '18px',
                          color: '#BF5927',
                          fontWeight: '700',
                          flexShrink: 0,
                        }}
                      >
                        {userGrade}
                      </Small>
                      <Small
                        sx={{
                          fontSize: isMobile ? '16px' : '20px',
                          color: theme.palette.text.primary,
                          fontWeight: '600',
                          flexShrink: 0,
                        }}
                      >
                        등급입니다.
                      </Small>
                    </Box>
                  </Box>
                  <Small
                    sx={{
                      fontSize: isMobile ? '12px' : '13px',
                      color: theme.palette.primary.greytxt,
                    }}
                  >
                    {tagline}
                  </Small>
                </Box>
              </FlexRowAlign>

              {/* Action Buttons - Follow and Share (Desktop only) */}
              {!isTablet && (
                <FlexRowAlign gap={1}>
                  {/* Share Dropdown */}
                  <Box ref={shareDropdownRef} sx={{ position: 'relative' }}>
                    <Box
                      onClick={handleShareMenuToggle}
                      sx={{
                        backgroundColor: '#fff',
                        border: `1px solid ${theme.palette.primary.borderColor}`,
                        borderRadius: '20px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        minWidth: '120px',
                        justifyContent: 'space-between',
                        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <Small
                        sx={{
                          color: theme.palette.primary.mainBlue,
                          fontSize: '14px',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        상태 : 정상
                      </Small>
                      <ChevronDown
                        size={16}
                        style={{
                          color: theme.palette.primary.mainBlue,
                          flexShrink: 0,
                          transform: shareDropdownOpen
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </Box>
                    {shareDropdownOpen && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          backgroundColor: 'white',
                          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                          borderRadius: '8px',
                          zIndex: 1000,
                          minWidth: '150px',
                          mt: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          onClick={handleShareMenuClose}
                          sx={{
                            padding: '8px 16px',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#f5f5f5' },
                          }}
                        >
                          <Small sx={{ fontSize: '14px' }}>공유하기</Small>
                        </Box>
                        <Box
                          onClick={handleShareMenuClose}
                          sx={{
                            padding: '8px 16px',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#f5f5f5' },
                          }}
                        >
                          <Small sx={{ fontSize: '14px' }}>링크 복사</Small>
                        </Box>
                        <Box
                          onClick={handleShareMenuClose}
                          sx={{
                            padding: '8px 16px',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#f5f5f5' },
                          }}
                        >
                          <Small sx={{ fontSize: '14px' }}>다른 옵션</Small>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </FlexRowAlign>
              )}
            </FlexRowAlign>

            {/* Action Buttons - Follow and Share (Tablet/Mobile - separate row) */}
            {isTablet && (
              <FlexRowAlign gap={1} justifyContent="flex-start">
                {/* Follow Button */}
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.primary.borderColor}`,
                    borderRadius: '100px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                  }}
                >
                  <Small
                    sx={{
                      color: theme.palette.primary.greytxt,
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    팔로우 <UserPlus size={16} />
                  </Small>
                </Box>

                {/* Share Dropdown */}
                <Box ref={shareDropdownRef} sx={{ position: 'relative' }}>
                  <Box
                    onClick={handleShareMenuToggle}
                    sx={{
                      backgroundColor: '#fff',
                      border: `1px solid ${theme.palette.primary.borderColor}`,
                      borderRadius: '20px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      minWidth: '120px',
                      justifyContent: 'space-between',
                      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <Small
                      sx={{
                        color: theme.palette.primary.mainBlue,
                        fontSize: '14px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      상태 : 정상
                    </Small>
                    <ChevronDown
                      size={16}
                      style={{
                        color: theme.palette.primary.mainBlue,
                        flexShrink: 0,
                        transform: shareDropdownOpen
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </Box>
                  {shareDropdownOpen && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        backgroundColor: 'white',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                        zIndex: 1000,
                        minWidth: '150px',
                        mt: 1,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        onClick={handleShareMenuClose}
                        sx={{
                          padding: '8px 16px',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#f5f5f5' },
                        }}
                      >
                        <Small sx={{ fontSize: '14px' }}>공유하기</Small>
                      </Box>
                      <Box
                        onClick={handleShareMenuClose}
                        sx={{
                          padding: '8px 16px',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#f5f5f5' },
                        }}
                      >
                        <Small sx={{ fontSize: '14px' }}>링크 복사</Small>
                      </Box>
                      <Box
                        onClick={handleShareMenuClose}
                        sx={{
                          padding: '8px 16px',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#f5f5f5' },
                        }}
                      >
                        <Small sx={{ fontSize: '14px' }}>다른 옵션</Small>
                      </Box>
                    </Box>
                  )}
                </Box>
              </FlexRowAlign>
            )}
          </Box>
          <FlexRowAlign justifyContent="space-between" width="100%">
            {/* Stats Row - Inside the card, under profile info */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                gap: isMobile ? '8px' : isTablet ? '8px' : '20px',
                justifyContent: isTablet ? 'space-between' : 'flex-start',
                marginBottom: 3,
                width: '100%',
                overflow: 'hidden',
              }}
            >
              <StatItem label="팔로워" value={stats.followers} />
              <StatItem label="팔로잉" value={stats.following} />
              <StatItem label="게시글" value={stats.posts} />
              <StatItem label="댓글" value={stats.comments} />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                gap: isMobile ? '8px' : isTablet ? '8px' : '10px',
                justifyContent: isTablet ? 'space-between' : 'flex-end',
                marginBottom: 3,
                width: '100%',
                overflow: 'hidden',
              }}
            >
              <Small
                sx={{
                  color: theme.palette.primary.greytxt,
                  fontSize: isMobile ? '11px' : !isTablet ? '14.5px' : '13px',
                  whiteSpace: 'nowrap',
                }}
              >
                유료결제 :
              </Small>
              <Small
                sx={{
                  color: theme.palette.primary.mainBlue,
                  fontSize: isMobile ? '11px' : !isTablet ? '14.5px' : '13px',
                  whiteSpace: 'nowrap',
                  mr: '10px',
                }}
              >
                프리미엄 지원사업 알림 서비스
              </Small>
              <Small
                sx={{
                  color: theme.palette.primary.greytxt,
                  fontSize: isMobile ? '11px' : !isTablet ? '14.5px' : '13px',
                  whiteSpace: 'nowrap',
                }}
              >
                카톡알림:
              </Small>
              <Small
                sx={{
                  color: theme.palette.primary.mainBlue,
                  fontSize: isMobile ? '11px' : !isTablet ? '14.5px' : '13px',
                  whiteSpace: 'nowrap',
                }}
              >
                미구독
              </Small>
            </Box>
          </FlexRowAlign>
        </Box>
      </Box>
    </StyledCard>
  );
};

export default UserProfileCard;
